/**
 * Task domain event schemas.
 *
 * Typed payload validation for all task-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module task.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Payload schemas ─────────────────────────────────────────────────────

const TimeTrackingPayload = z.object({
  taskId: z.string(),
  userId: z.string(),
  timestamp: z.string(),
  duration: z.number().optional(),
  sessionId: z.string().optional(),
  previousDuration: z.number().optional(),
  isBillable: z.boolean().optional(),
  source: z.string(),
})

const DependencyPayload = z.object({
  dependencyId: z.string().optional(),
  predecessorTaskId: z.string(),
  successorTaskId: z.string(),
  dependencyType: z.string(),
  lagTime: z.number().optional(),
  userId: z.string(),
  circularDependencyChain: z.array(z.string()).optional(),
  source: z.string(),
})

const EstimationPayload = z.object({
  taskId: z.string(),
  estimatedDuration: z.number(),
  actualDuration: z.number().optional(),
  confidenceLevel: z.number().optional(),
  estimationMethod: z.string().optional(),
  accuracy: z.number().optional(),
  userId: z.string(),
  source: z.string(),
})

const TasksExtractedPayload = z.object({
  documentId: z.string(),
  tasks: z.array(z.object({
    title: z.string(),
    level: z.number().optional(),
    documentId: z.string().optional(),
  })),
  sourceEventId: z.string().optional(),
  source: z.string().optional(),
})

const CalendarEventSuggestedPayload = z.object({
  taskId: z.string(),
  taskTitle: z.string(),
  suggestedEventTitle: z.string(),
  suggestedEventDate: z.string(),
  sourceEventId: z.string().optional(),
  source: z.string().optional(),
})

const TaskCompletedPayload = z.object({
  taskId: z.string(),
  title: z.string().optional(),
  summary: z.string().optional(),
  sessionId: z.string().optional(),
  agentName: z.string().optional(),
  source: z.string(),
})

// ── Event schemas ───────────────────────────────────────────────────────

export const TimeTrackingStartedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_tracking_started'),
  payload: TimeTrackingPayload,
})

export const TimeTrackingStoppedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_tracking_stopped'),
  payload: TimeTrackingPayload,
})

export const TimeTrackingPausedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_tracking_paused'),
  payload: TimeTrackingPayload,
})

export const TimeTrackingResumedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_tracking_resumed'),
  payload: TimeTrackingPayload,
})

export const TimeTrackingTrackedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_tracking_tracked'),
  payload: TimeTrackingPayload,
})

export const TimeSessionSavedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('time_session_saved'),
  payload: TimeTrackingPayload,
})

export const DependencyCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_created'),
  payload: DependencyPayload,
})

export const DependencyUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_updated'),
  payload: DependencyPayload,
})

export const DependencyDeletedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_deleted'),
  payload: DependencyPayload,
})

export const DependencyCircularDetectedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_circular_detected'),
  payload: DependencyPayload,
})

export const DependencyCircularResolvedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_circular_resolved'),
  payload: DependencyPayload,
})

export const CriticalPathCalculatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_critical_path_calculated'),
  payload: DependencyPayload,
})

export const TaskSlackCalculatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('task_slack_calculated'),
  payload: DependencyPayload,
})

export const DependencyGraphUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('dependency_graph_updated'),
  payload: DependencyPayload,
})

export const EstimationCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('estimation_created'),
  payload: EstimationPayload,
})

export const EstimationUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('estimation_updated'),
  payload: EstimationPayload,
})

export const EstimationAccuracyLoggedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('estimation_accuracy_logged'),
  payload: EstimationPayload,
})

export const EstimationAiSuggestedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('estimation_ai_suggested'),
  payload: EstimationPayload,
})

export const TasksExtractedFromDocumentEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('tasks_extracted_from_document'),
  payload: TasksExtractedPayload,
})

export const CalendarEventSuggestedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('calendar_event_suggested'),
  payload: CalendarEventSuggestedPayload,
})

export const TaskCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('task'),
  type: z.literal('task_completed'),
  payload: TaskCompletedPayload,
})

// ── All task events ─────────────────────────────────────────────────────

export const TaskDomainEvents = [
  TimeTrackingStartedEvent,
  TimeTrackingStoppedEvent,
  TimeTrackingPausedEvent,
  TimeTrackingResumedEvent,
  TimeTrackingTrackedEvent,
  TimeSessionSavedEvent,
  DependencyCreatedEvent,
  DependencyUpdatedEvent,
  DependencyDeletedEvent,
  DependencyCircularDetectedEvent,
  DependencyCircularResolvedEvent,
  CriticalPathCalculatedEvent,
  TaskSlackCalculatedEvent,
  DependencyGraphUpdatedEvent,
  EstimationCreatedEvent,
  EstimationUpdatedEvent,
  EstimationAccuracyLoggedEvent,
  EstimationAiSuggestedEvent,
  TasksExtractedFromDocumentEvent,
  CalendarEventSuggestedEvent,
  TaskCompletedEvent,
] as const
